import flatten from 'lodash/flatten'
import React, { useCallback, useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useNavigate } from 'react-router-dom'
import { Column, SortingRule } from 'react-table'
import { toast } from 'react-hot-toast'
import { useMutation } from 'react-query'
import isEqual from 'lodash/isEqual'

import { useLocations } from '../hooks/useLocations'
import { useParamsToObj } from '../hooks/useParamsToObj'
import { Place } from '../types/utils'
import { LocationFilter } from '../types/locations'
import placeService from '../services/placeService'
import { queryClient } from '../utils/react-query'
import { useUser } from '../hooks/useUser'

import TableSkeleton from '../components/TableSkeleton'
import Table from '../components/Table'
import Spinner from '../components/Spinner'
import FilterDrawer from '../components/Locations/FilterDrawer'
import LocationHeader from '../components/LocationSingle/LocationHeader'
import DeleteButton from '../components/DeleteButton'
import { BuzzlUsersError } from '../types/buzzl-user'

const initialFilter: LocationFilter = {
  rating: [],
  status: [],
  customers: [],
  locations: [],
  categories: [],
  addresses: [],
  nbExperiences: {
    min: 0,
    max: 1000,
  },
}

const Locations: React.FC = () => {
  const user = useUser()
  const urlParams = useParamsToObj(['search'])
  const [filter, setFilter] = useState<LocationFilter>(initialFilter)
  const [orderByFilter, setOrderBy] = useState<SortingRule<Place>[]>([])
  const [filterDrawerStatus, setFilterDrawerStatus] = useState(false)
  const locations = useLocations(urlParams, filter, orderByFilter)
  const navigate = useNavigate()
  const { ref, inView } = useInView({
    rootMargin: '100px 0px 0px 0px',
  })
  const respondToClaimRequest = useMutation(placeService.respondToClaimRequest, {
    onSuccess: async (data) => {
      queryClient.refetchQueries(['locations'])
      queryClient.refetchQueries(['location', data.id])
    },
    onError: (e) => {
      const error = e as BuzzlUsersError
      error.response.errors.map((er) =>
        toast.error(er.message || 'Something went wrong, if the error persist please contact support.')
      )
    },
  })

  const { hasNextPage, fetchNextPage } = locations

  const handleCancelClaim = (id: string) => async () => {
    try {
      await respondToClaimRequest.mutateAsync({ placeId: id, action: 'CANCEL' })
    } catch (e) {
      console.log(e)
    }
  }

  const columns = React.useMemo<Column<Place>[]>(
    () => [
      {
        id: 'name',
        Header: 'Location',
        accessor: ({ name }) => (
          <span style={{ maxWidth: 137 }} className="block truncate">
            {name}
          </span>
        ),
      },
      {
        id: 'customerName',
        Header: 'Customer',
        accessor: ({ customer, claimStatus, requestedClaimBy }) => (
          <span style={{ maxWidth: 85 }} className="block truncate">
            {claimStatus === 'PENDING' ? requestedClaimBy?.name : customer?.name}
          </span>
        ),
      },
      {
        id: 'nbExperiences',
        Header: 'Experiences',
        accessor: 'nbExperiences',
      },
      {
        id: 'address',
        Header: 'Address',
        accessor: ({ address }) => (
          <span style={{ maxWidth: 184 }} className="block truncate">
            {address}
          </span>
        ),
      },
      {
        id: 'averageEvaluation',
        Header: 'Rating',
        accessor: ({ averageEvaluation }) => {
          if (averageEvaluation === 'NEGATIVE') {
            return <span className="tag negative-tag">Negative</span>
          } else if (averageEvaluation === 'POSITIVE') {
            return <span className="tag positive-tag">Positive</span>
          } else {
            return <span className="tag alert-tag">Neutral</span>
          }
        },
      },
      {
        id: 'category',
        Header: 'Category',
        accessor: ({ categories }) => categories.map((c) => c.name).join(','),
      },
      {
        id: 'claimStatus',
        Header: 'Status',
        accessor: ({ claimStatus }) => {
          if (claimStatus === 'UNCLAIMED') {
            return <span className="tag bg-gray-100 text-gray-800">Unclaimed</span>
          } else if (claimStatus === 'PENDING') {
            return <span className="tag alert-tag">Pending</span>
          } else {
            return <span className="tag positive-tag">Claimed</span>
          }
        },
      },
      {
        Header: ' ',
        disableSortBy: true,
        accessor: ({ id, claimStatus }) => {
          if (user.data?.type === 'CUSTOMER' && user.data?.role !== 'OPERATOR' && claimStatus === 'PENDING') {
            return (
              <DeleteButton
                className="text-link"
                modalTitle="Cancel Request?"
                modalText="You are about to cancel the selected requests for this location. Are you sure you want to cancel?"
                disabled={respondToClaimRequest.isLoading}
                onClick={handleCancelClaim(id)}
                buttonText="Confirm"
              >
                Cancel
              </DeleteButton>
            )
          }

          return (
            <button
              onClick={() => {
                navigate(`/locations/${id}`)
              }}
              className="text-link"
            >
              View
            </button>
          )
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    const init = async () => {
      if (inView && hasNextPage) {
        await fetchNextPage()
      }
    }
    init()
  }, [inView, hasNextPage, fetchNextPage])

  // Handle react-table sorting changes (in Table.tsx)
  const handleSortByChange = useCallback(
    (orderBy: SortingRule<any>[]) => {
      if (isEqual(orderByFilter, orderBy)) return

      setOrderBy(orderBy)
    },
    [orderByFilter]
  )

  const locationsData = flatten(locations.data?.pages)

  const resetFilters = () => {
    setFilter(initialFilter)
    navigate({
      search: ``,
    })
  }

  const handlePendingFilter = () => {
    setFilter({
      ...initialFilter,
      status: ['PENDING'],
    })
  }

  const filterCount = (): number => {
    let counter = 0

    if (filter.addresses.length > 0) counter++
    if (filter.rating.length > 0) counter++
    if (filter.status.length > 0) counter++
    if (filter.categories.length > 0) counter++
    if (filter.customers.length > 0) counter++
    if (filter.locations.length > 0) counter++
    if (filter.nbExperiences.max < 1000 || filter.nbExperiences.min > 0) counter++

    return counter
  }

  if (locations.isLoading) {
    return (
      <>
        <LocationHeader
          filterBtnAction={() => {}}
          filterPending={() => {}}
          urlParams={urlParams}
          nbOfExtraFilter={filterCount()}
        />
        <TableSkeleton headers={['Location', 'Customer', 'Experiences', 'Address', 'Rating', 'Category']} />
      </>
    )
  }

  return (
    <div>
      <FilterDrawer
        open={filterDrawerStatus}
        onClose={() => setFilterDrawerStatus(false)}
        filter={filter}
        setFilter={setFilter}
        resetFilter={resetFilters}
      />
      <LocationHeader
        filterPending={handlePendingFilter}
        filterBtnAction={() => setFilterDrawerStatus(true)}
        urlParams={urlParams}
        nbOfExtraFilter={filterCount()}
      />
      <Table columns={columns} data={locationsData} onSortBy={handleSortByChange} />
      {hasNextPage ? (
        <div ref={ref} className="mt-4 mb-4 flex flex-col items-center text-center">
          <Spinner className="h-8 w-8 text-gray-400" />
          <span className="text-gray-400">Loading more...</span>
        </div>
      ) : null}
    </div>
  )
}

export default Locations
